import { createFileRoute } from '@tanstack/react-router';
import { Badge, Card, Group, Loader, Stack, Switch, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';

/**
 * Tier-1 growth feature toggles. All default OFF. The "Needs Meta approval"
 * features stay dark until the platform's Meta app has the required access — the
 * toggle only gates our code, it doesn't grant Meta permissions.
 */
function FeaturesSettings() {
  const utils = trpc.useUtils();
  const features = trpc.settings.features.list.useQuery();
  const setFlag = trpc.settings.features.set.useMutation({
    onSuccess: () => {
      void utils.settings.features.list.invalidate();
    },
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });

  if (features.isPending) return <Loader />;

  return (
    <Stack gap="md">
      <div>
        <Title order={2}>Features</Title>
        <Text c="dimmed" size="sm">
          Turn features on or off. Features marked “Needs Meta approval” need the platform’s Meta
          access before they work — enabling them early is safe, but they stay inactive until
          approved.
        </Text>
      </div>
      <Stack gap="sm">
        {(features.data ?? []).map((f) => (
          <Card key={f.key} withBorder padding="md">
            <Group justify="space-between" wrap="nowrap" align="flex-start">
              <Stack gap={4} style={{ minWidth: 0 }}>
                <Group gap="xs">
                  <Text fw={600}>{f.label}</Text>
                  {f.gated && (
                    <Badge color="yellow" variant="light" size="sm">
                      Needs Meta approval
                    </Badge>
                  )}
                </Group>
                <Text size="sm" c="dimmed">
                  {f.description}
                </Text>
                {f.gated && f.requirement && (
                  <Text size="xs" c="dimmed">
                    Before it works: {f.requirement}
                  </Text>
                )}
              </Stack>
              <Switch
                checked={f.enabled}
                disabled={setFlag.isPending}
                onChange={(e) => setFlag.mutate({ key: f.key, enabled: e.currentTarget.checked })}
              />
            </Group>
          </Card>
        ))}
      </Stack>
    </Stack>
  );
}

export const Route = createFileRoute('/vendor/settings/features')({ component: FeaturesSettings });
